<template>
  <div class="w-full block">
    <div v-if="showExpiredBanner" class="flex md:justify-center mt-6 mx-4 md:mx-2 md:text-center">
      <div class="flex flex-col md:items-center">
        <div class="flex items-center align-middle w-full flex-nowrap justify-start md:justify-center">
          <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter align-middle flex items-center leading-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6 mr-1 hidden md:block"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
              />
            </svg>
            <div class="flex flex-wrap items-center">You are past the cutoff</div>
          </h2>
        </div>
        <div class="flex items-center">
          <button
            v-if="openForOrders"
            class="mt-2 relative px-3 py-2 rounded-lg flex items-center border border-brand-800 hover:bg-brand-100 bg-white"
            @click="$emit('order-different-day')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="mr-1 h-4 w-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
              />
            </svg>
            <div class="inline-block font-semibold text-xs">Order for a different day</div>
          </button>
          <button
            v-else
            class="mt-2 relative px-3 py-2 rounded-lg flex items-center border border-brand-800 hover:bg-brand-100 bg-white"
            @click="$emit('go-home')"
          >
            <svg
              class="mr-1 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
            </svg>
            <div class="inline-block font-semibold text-xs">Go back</div>
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="showConfirmedBanner" class="flex md:justify-center mt-6 mx-4 md:mx-2 md:text-center">
      <div class="flex flex-col md:items-center">
        <div class="flex items-center align-middle w-full flex-nowrap justify-start md:justify-center">
          <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter align-middle flex items-center leading-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6 mr-1 hidden md:block"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div class="flex flex-wrap items-center">
              <span v-if="arrivalDateFormatted">Your order for {{ arrivalDateFormatted }} is confirmed</span>
              <span v-else>Your order is confirmed</span>
            </div>
          </h2>
        </div>
        <h3 class="mt-2 text-md text-gray-brand-700 w-full leading-5">
          <span v-if="autochargeTimeFormatted">
            Edit
            <span v-if="offerSkipButton">or skip</span>
            your order until {{ autochargeTimeFormatted }}. Pay to lock in your cart.
          </span>
        </h3>
        <div class="flex items-center space-x-2">
          <button
            v-if="offerSkipButton"
            class="mt-2 relative px-3 py-2 rounded-lg flex items-center border border-brand-800 hover:bg-brand-100 bg-white"
            @click="$emit('skip')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-4 h-4 mr-1"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
            </svg>
            <div class="inline-block font-semibold text-xs">Skip order</div>
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="!openForOrders" class="flex md:justify-center mt-6 mx-4 md:mx-2 md:text-center">
      <div class="flex flex-col md:items-center">
        <div class="flex items-center align-middle w-full flex-nowrap justify-start md:justify-center">
          <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter align-middle flex items-center leading-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6 mr-1"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
              />
            </svg>
            <div class="flex flex-wrap items-center">Ordering is closed</div>
          </h2>
        </div>
        <h3 class="mt-2 text-md text-gray-brand-700 w-full leading-5">
          <span v-if="formattedTimeOrderingOpens">Ordering opens {{ formattedTimeOrderingOpens }}.</span>
          <span v-else>These items are not currently available for purchase.</span>
        </h3>
      </div>
    </div>

    <div v-else class="flex md:justify-center mt-6 mx-4 md:mx-2 md:text-center">
      <div class="flex flex-col md:items-center">
        <div class="flex items-center align-middle w-full flex-nowrap justify-start md:justify-center">
          <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter align-middle flex items-center leading-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6 mr-1 hidden md:block"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
              />
            </svg>
            <div class="flex flex-wrap items-center">
              Your upcoming
              <span v-if="arrivalDateFormatted">&nbsp;{{ arrivalDateFormatted }}&nbsp;</span>
              order
            </div>
          </h2>
        </div>
        <h3 class="mt-2 text-md text-gray-brand-700 w-full leading-5">
          <span v-if="autochargeTimeFormatted">
            Edit
            <span v-if="offerSkipButton">or skip</span>
            your order until {{ autochargeTimeFormatted }}. Pay to lock in your cart.
          </span>
          <span v-else-if="cutoffFormatted">Place your order by {{ cutoffFormatted }}.</span>
        </h3>
        <div class="flex items-center space-x-2">
          <button
            v-if="offerSkipButton"
            class="mt-2 relative px-3 py-2 rounded-lg flex items-center border border-brand-800 hover:bg-brand-100 bg-white"
            @click="$emit('skip')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-4 h-4 mr-1"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
            </svg>
            <div class="inline-block font-semibold text-xs">Skip order</div>
          </button>
          <button
            v-if="cartIsEmpty"
            class="mt-2 relative px-3 py-2 rounded-lg flex items-center border border-brand-800 hover:bg-brand-100 bg-white"
            @click="$emit('create-new-order')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="mr-1 h-4 w-4"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
              />
            </svg>
            <div class="inline-block font-semibold text-xs">Create new order</div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
let strftime = require('strftime')

export default {
  name: 'V2BottleCycleBanner',

  props: {
    arrivalDate: {
      type: String,
      default: null
    },
    cutoff: {
      type: String,
      default: null
    },
    expired: {
      type: Boolean,
      default: false
    },
    autocharge: {
      type: Boolean,
      default: false
    },
    autochargeDatetime: {
      type: String,
      default: null
    },
    confirmed: {
      type: Boolean,
      default: false
    },
    blockSkip: {
      type: Boolean,
      default: false
    },
    openForOrders: {
      type: Boolean,
      default: false
    },
    nextSlotOpens: {
      type: String,
      default: null
    },
    cartIsEmpty: {
      type: Boolean,
      default: false
    }
  },

  emits: ['skip', 'order-different-day', 'go-home', 'create-new-order'],

  computed: {
    showExpiredBanner() {
      return this.expired
    },
    showConfirmedBanner() {
      return this.autocharge && this.confirmed
    },
    offerSkipButton() {
      return this.autocharge && !this.blockSkip
    },
    arrivalDateFormatted() {
      if (!this.arrivalDate) return null
      const date = new Date(this.arrivalDate)
      return strftime('%A, %B %-d', date)
    },
    cutoffFormatted() {
      if (!this.cutoff) return null
      const date = new Date(this.cutoff)
      return strftime('%A, %B %-d at ', date) + strftime('%-I:%M', date) + strftime('%p', date).toLowerCase()
    },
    autochargeTimeFormatted() {
      if (!this.autochargeDatetime || !this.autocharge) return null
      const date = new Date(this.autochargeDatetime)
      return strftime('%A, %B %-d at ', date) + strftime('%-I:%M', date) + strftime('%p', date).toLowerCase()
    },
    formattedTimeOrderingOpens() {
      if (!this.nextSlotOpens) return null
      const date = new Date(this.nextSlotOpens)
      return strftime('%A, %B %-d at ', date) + strftime('%-I:%M', date) + strftime('%p', date).toLowerCase()
    }
  }
}
</script>
